import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, CalendarClock, Loader2, SendHorizonal, X } from 'lucide-react';
import { requestSendScheduledEmailNow } from '../../lib/scheduledEmailSend';
import type { ScheduledEmailRow } from './types';

interface SendScheduledEmailNowModalProps {
  isOpen: boolean;
  row: ScheduledEmailRow | null;
  /** Automation name when this send is from an enrollment. */
  automationName?: string | null;
  onClose: () => void;
  onSuccess: () => void;
}

function formatWhen(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

/** Human-readable delay until scheduled_at (e.g. "about 3 days from now"). */
function describeScheduledIn(scheduledAtIso: string, nowMs: number): string {
  const targetMs = new Date(scheduledAtIso).getTime();
  if (Number.isNaN(targetMs)) return 'on its scheduled date';

  const diffSec = Math.round((targetMs - nowMs) / 1000);
  if (diffSec <= 60) return 'within about a minute';
  if (diffSec < 0) {
    return 'the scheduled time has passed; the regular batch sender would deliver it soon';
  }

  const daySec = 86400;
  const hourSec = 3600;
  if (diffSec >= daySec) {
    const days = Math.round(diffSec / daySec);
    return `about ${days} day${days === 1 ? '' : 's'} from now`;
  }
  if (diffSec >= hourSec) {
    const hours = Math.round(diffSec / hourSec);
    return `about ${hours} hour${hours === 1 ? '' : 's'} from now`;
  }
  const mins = Math.max(1, Math.round(diffSec / 60));
  return `about ${mins} minute${mins === 1 ? '' : 's'} from now`;
}

function rowLabel(row: ScheduledEmailRow): string {
  return row.name?.trim() || row.subject?.trim() || 'Scheduled email';
}

export default function SendScheduledEmailNowModal({
  isOpen,
  row,
  automationName,
  onClose,
  onSuccess,
}: SendScheduledEmailNowModalProps) {
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const timing = useMemo(() => {
    if (!row?.scheduled_at) return null;
    const nowMs = Date.now();
    return {
      formatted: formatWhen(row.scheduled_at),
      relative: describeScheduledIn(row.scheduled_at, nowMs),
    };
  }, [row?.scheduled_at]);

  useEffect(() => {
    if (!isOpen) return;
    setError(null);
    setSending(false);
  }, [isOpen, row?.id]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !sending) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose, sending]);

  const handleSendNow = async () => {
    if (!row) return;
    setSending(true);
    setError(null);
    try {
      await requestSendScheduledEmailNow(row.id);
      onSuccess();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Send now failed');
    } finally {
      setSending(false);
    }
  };

  if (!isOpen || !row) return null;

  const label = rowLabel(row);
  const viaAutomation = !!row.automation_enrollment_id;

  return (
    <div
      className="fixed inset-0 z-50 flex items-stretch sm:items-center justify-center bg-black/50 p-0 sm:p-4"
      onClick={() => {
        if (!sending) onClose();
      }}
    >
      <div
        className="bg-white dark:bg-gray-800 shadow-xl w-full max-w-lg flex flex-col min-h-0 h-[100dvh] max-h-[100dvh] rounded-none sm:h-auto sm:max-h-[min(90vh,900px)] sm:rounded-xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="send-now-modal-title"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700 shrink-0">
          <h2
            id="send-now-modal-title"
            className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2"
          >
            <SendHorizonal className="w-5 h-5 text-emerald-500" />
            Send now?
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={sending}
            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-50"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-4 space-y-4 text-sm text-gray-700 dark:text-gray-200">
          <p>
            <span className="font-medium text-gray-900 dark:text-white">{label}</span>
            {row.subject?.trim() && row.name?.trim() ? (
              <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                Subject: {row.subject}
              </span>
            ) : null}
          </p>

          {viaAutomation && automationName ? (
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Part of automation: <span className="font-medium">{automationName}</span>
            </p>
          ) : null}

          <div className="rounded-lg border border-sky-200 dark:border-sky-800 bg-sky-50/80 dark:bg-sky-900/20 px-4 py-3 space-y-2">
            <div className="flex items-start gap-2">
              <CalendarClock className="w-4 h-4 text-sky-600 dark:text-sky-400 mt-0.5 shrink-0" />
              <div>
                <p className="font-medium text-sky-900 dark:text-sky-100">On the normal schedule</p>
                {timing ? (
                  <p className="mt-1 text-sky-800/90 dark:text-sky-200/90">
                    Normally it sends on{' '}
                    <span className="font-medium">{timing.formatted}</span> (
                    <span className="font-medium">{timing.relative}</span>).
                  </p>
                ) : (
                  <p className="mt-1 text-sky-800/90 dark:text-sky-200/90">
                    This email will go out on its scheduled date and time.
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50/80 dark:bg-amber-900/20 px-4 py-3">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
              <div>
                <p className="font-medium text-amber-900 dark:text-amber-100">If you send now</p>
                <p className="mt-1 text-amber-800/90 dark:text-amber-200/90">
                  We queue an immediate send — usually within seconds — and{' '}
                  <span className="font-medium">do not wait</span> for that scheduled date.
                  {viaAutomation ? (
                    <>
                      {' '}
                      Other steps in the automation keep their own schedules unless you send those
                      separately.
                    </>
                  ) : null}
                </p>
              </div>
            </div>
          </div>

          {error ? (
            <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
          ) : null}
        </div>

        <div className="shrink-0 flex justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
          <button
            type="button"
            onClick={onClose}
            disabled={sending}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
          >
            Keep scheduled time
          </button>
          <button
            type="button"
            onClick={() => void handleSendNow()}
            disabled={sending}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 rounded-lg transition-colors"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <SendHorizonal className="w-4 h-4" />}
            {sending ? 'Sending…' : 'Send now'}
          </button>
        </div>
      </div>
    </div>
  );
}
